// src/utils/labTestSuggestion.js
//
// A doctor's lab-test suggestion, carried as an ordinary care-plan action item.
//
// WHY AN ACTION ITEM, NOT AN ORDER
// --------------------------------
// A doctor cannot place a lab order: orders are paid for from the patient's
// wallet (or Razorpay) and sent to the partner lab, which is the patient's or
// the navigator's action (PatientOrderLabTestPage / LabTestFormPage). So a
// suggestion is a prioritised, dated care-plan item that points the patient at
// the existing order flow. It travels through the endpoint, the care-plan page,
// the Today attention list and the bell that action items already use.
//
// The description starts with a fixed phrase so the patient screens can tell a
// suggestion apart and offer "Order these tests". That phrase is the whole
// contract between the two sides, so it lives here, in one place. Moving the
// codes into their own backend fields (e.g. suggested panel/marker codes on the
// action item) would let the patient side stop reading the sentence; until then
// this is deliberately small.

export const LAB_TEST_SUGGESTION_PREFIX = 'Lab tests suggested:';

/** Where the patient goes to act on a suggestion. */
export const LAB_TEST_ORDER_PATH = '/patient/lab-tests/order';

export function isLabTestSuggestion(description) {
  return typeof description === 'string' && description.startsWith(LAB_TEST_SUGGESTION_PREFIX);
}

/** YYYY-MM-DD, `weeks` from today, in the browser's local calendar. */
export function dueDateInWeeks(weeks, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + weeks * 7);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * @param {object}   args
 * @param {string[]} args.markers     marker names to retest
 * @param {string}   [args.panelName] Previu panel display name, if one is suggested
 * @param {number}   args.weeks       "repeat in N weeks"
 * @param {string}   [args.note]      free-text addition
 * @returns {string} the action-item description (kept well under the 500-char column)
 */
export function buildLabTestSuggestion({ markers = [], panelName, weeks, note }) {
  const parts = [];
  if (markers.length) parts.push(`${LAB_TEST_SUGGESTION_PREFIX} ${markers.join(', ')}.`);
  if (panelName) {
    parts.push(markers.length ? `Panel: ${panelName}.` : `${LAB_TEST_SUGGESTION_PREFIX} ${panelName}.`);
  }
  parts.push(`Repeat in ${weeks} week${weeks === 1 ? '' : 's'}.`);
  if (note && note.trim()) parts.push(note.trim());
  return parts.join(' ').slice(0, 480);
}
